<template>
  <section class="overflow-hidden rounded-[28px] border border-[#eee4da] bg-white shadow-sm">
    <div class="flex flex-wrap items-center justify-between gap-4 border-b border-[#eee4da] px-10 py-6">
      <div class="flex items-center gap-4">
        <button type="button" class="text-xl text-muted transition hover:text-brand" @click="router.push('/dashboard')">
          <i class="ri-arrow-left-line"></i>
        </button>
        <div>
          <div class="flex items-center gap-3">
            <h2 class="text-[2rem] font-semibold text-ink">{{ isEditMode ? businessLabel : 'New business' }}</h2>
            <span class="rounded-full bg-[#F6EEE5] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand">Business</span>
          </div>
          <p class="mt-1 text-sm text-muted">Legal entity</p>
        </div>
      </div>

 
    </div>

    <div class="grid min-h-[760px] bg-[#fdf8f3] lg:grid-cols-[210px_1fr]">
      <aside class="border-r border-[#eee4da] bg-white px-0 py-3">
        <button
          v-for="item in tabs"
          :key="item.id"
          type="button"
          class="flex w-full items-center justify-between px-6 py-4 text-left text-[1.02rem] transition"
          :class="activeTab === item.id ? 'border-r-[3px] border-brand bg-[#fbf1e6] font-semibold text-brand' : 'text-ink hover:bg-[#fcf5ed]'"
          @click="setTab(item.id)"
        >
          <span>{{ item.label }}</span>
        </button>
      </aside>

      <div class="px-8 py-8 lg:px-9">
        <div v-if="loadError" class="mb-6 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
          {{ loadError }}
        </div>

        <div v-if="activeTab === 'business'" class="space-y-6">
          <div class="rounded-2xl border border-[#f5d27d] bg-[#fff7de] px-4 py-4 text-sm text-[#996515]">
            <span class="font-semibold">Private.</span>
            Legal name, tax, and registration details are never shown to customers.
          </div>

          <section class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
            <h3 class="font-serif text-[2rem] font-semibold text-ink">Legal business details</h3>
            <div class="mt-5 grid gap-4 md:grid-cols-2">
              <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                Legal business name <span class="text-brand">*</span>
                <input v-model.trim="businessForm.businessName" type="text" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal text-ink focus:border-brand focus:outline-none">
                <span class="mt-2 block text-xs font-normal normal-case text-muted">Exactly as per registration certificate</span>
                <p v-if="businessFieldErrors.businessName" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ businessFieldErrors.businessName }}</p>
              </label>
              <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                Entity type <span class="text-brand">*</span>
                <select v-model="businessMeta.entityType" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal text-ink focus:border-brand focus:outline-none">
                  <option value="">Select...</option>
                  <option>Sole Proprietorship</option>
                  <option>Partnership</option>
                  <option>Private Limited</option>
                  <option>LLP</option>
                </select>
                <p v-if="businessFieldErrors.entityType" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ businessFieldErrors.entityType }}</p>
              </label>
              <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                GST number <span class="text-brand">*</span>
                <input v-model.trim="businessMeta.gstNumber" type="text" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal text-ink focus:border-brand focus:outline-none">
                <p v-if="businessFieldErrors.gstNumber" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ businessFieldErrors.gstNumber }}</p>
              </label>
              <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                PAN <span class="text-brand">*</span>
                <input v-model.trim="businessMeta.pan" type="text" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal text-ink focus:border-brand focus:outline-none">
                <p v-if="businessFieldErrors.pan" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ businessFieldErrors.pan }}</p>
              </label>
              <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638] md:col-span-2">
                Trade license number <span class="text-brand">*</span>
                <input v-model.trim="businessMeta.tradeLicenseNumber" type="text" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal text-ink focus:border-brand focus:outline-none">
                <p v-if="businessFieldErrors.tradeLicenseNumber" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ businessFieldErrors.tradeLicenseNumber }}</p>
              </label>
            </div>
          </section>

          <section class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
            <h3 class="font-serif text-[2rem] font-semibold text-ink">Business profile</h3>
            <div class="mt-5 grid gap-4 md:grid-cols-2">
              <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                Owner name <span class="text-brand">*</span>
                <input v-model.trim="businessForm.ownerName" type="text" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal text-ink focus:border-brand focus:outline-none">
                <p v-if="businessFieldErrors.ownerName" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ businessFieldErrors.ownerName }}</p>
              </label>
              <div class="hidden md:block"></div>
              <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638] md:col-span-2">
                Business description <span class="text-brand">*</span>
                <textarea v-model.trim="businessForm.description" rows="5" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal text-ink focus:border-brand focus:outline-none"></textarea>
                <span class="mt-2 block text-xs font-normal normal-case text-muted">Use this to describe the business for internal setup and future listing content.</span>
                <p v-if="businessFieldErrors.description" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ businessFieldErrors.description }}</p>
              </label>
            </div>
          </section>

          <section class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
            <h3 class="font-serif text-[2rem] font-semibold text-ink">Brand image</h3>
            <div class="mt-5">
              <label class="flex cursor-pointer items-center justify-center rounded-3xl border border-dashed border-[#d8c8b8] bg-[#fffaf5] px-5 py-8 text-center transition hover:border-brand">
                <input type="file" accept="image/*" class="hidden" @change="handleBusinessImageChange">
                <span class="text-sm font-medium text-muted">Upload logo / cover image</span>
              </label>
              <p v-if="businessErrors.image" class="mt-2 text-xs font-semibold text-red-600">{{ businessErrors.image }}</p>

              <div v-if="businessImagePreview" class="mt-4 overflow-hidden rounded-3xl border border-gray-200 bg-white">
                <img :src="businessImagePreview" alt="Business preview" class="h-56 w-full object-cover">
                <div class="flex items-center justify-between gap-3 border-t border-gray-100 px-4 py-3">
                  <p class="truncate text-sm font-medium text-ink">{{ businessForm.imageName }}</p>
                  <button type="button" class="rounded-full border border-red-200 px-4 py-2 text-xs font-semibold text-red-600 transition hover:border-red-300" @click="removeBusinessImage">
                    Remove image
                  </button>
                </div>
              </div>
            </div>
          </section>

        </div>

        <div v-else-if="activeTab === 'contact'" class="space-y-6">
          <section class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
            <h3 class="font-serif text-[2rem] font-semibold text-ink">Contact & Address details</h3>
            <div class="mt-5 grid gap-4 md:grid-cols-2">
              <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638] md:col-span-2">
                Contact Person Name
                <input v-model.trim="contactForm.contactPersonName" type="text" placeholder="e.g. Riya Sharma" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              </label>
              <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                Email <span class="text-brand">*</span>
                <input v-model.trim="contactForm.email" type="email" placeholder="e.g. hello@yourbusiness.com" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                <p v-if="contactFieldErrors.email" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ contactFieldErrors.email }}</p>
              </label>
              <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                Website <span class="text-brand">*</span>
                <input v-model.trim="contactForm.website" type="text" placeholder="e.g. www.yourbusiness.com" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                <p v-if="contactFieldErrors.website" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ contactFieldErrors.website }}</p>
              </label>
              <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638] md:col-span-2">
                Address
                <div class="mt-2 flex flex-col gap-3 md:flex-row">
                  <div class="flex-1">
                    <input v-model.trim="contactForm.address" type="text" placeholder="e.g. 24 MG Road, Indiranagar, Bengaluru, Karnataka" class="w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                  </div>
                  <button
                    type="button"
                    class="inline-flex items-center justify-center gap-2 rounded-2xl border border-[#e5d8cc] bg-[#fcf7f2] px-5 py-3 text-sm font-semibold normal-case text-ink transition hover:border-brand hover:text-brand"
                    @click="openLocationPicker"
                  >
                    <i class="ri-map-pin-2-line"></i>
                    Choose on map
                  </button>
                </div>
                <span class="mt-2 block text-xs font-normal normal-case text-muted">Search, drag the pin, or use current location to fill address and coordinates.</span>
              </label>
              <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                Latitude
                <input v-model.trim="contactForm.latitude" type="text" placeholder="e.g. 12.9716" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              </label>
              <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                Longitude
                <input v-model.trim="contactForm.longitude" type="text" placeholder="e.g. 77.5946" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              </label>
              <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                Currency
                <input v-model.trim="contactForm.currency" type="text" placeholder="e.g. INR" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              </label>
              <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                Zone
                <input v-model.trim="contactForm.zone" type="text" placeholder="e.g. GMT+5:30" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              </label>
              <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                Timezone
                <input v-model.trim="contactForm.timezone" type="text" placeholder="e.g. Asia/Kolkata" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              </label>
              <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                Country
                <input v-model.trim="contactForm.country" type="text" placeholder="e.g. India" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              </label>
              <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                State
                <input v-model.trim="contactForm.state" type="text" placeholder="e.g. Karnataka" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              </label>
              <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                City
                <input v-model.trim="contactForm.city" type="text" placeholder="e.g. Bengaluru" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              </label>
            </div>

            <div class="mt-8 rounded-[24px] border border-[#eadfd4] bg-[#fcf7f2] p-5">
              <div class="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">Phone Numbers</p>
                  <p class="mt-1 text-sm text-muted">Choose a type for each number. Office, WhatsApp, and landline values are saved into their matching backend params.</p>
                </div>
                <button
                  type="button"
                  class="rounded-full border border-[#e5d8cc] bg-white px-4 py-2 text-xs font-semibold text-ink transition hover:border-brand hover:text-brand"
                  @click="addBusinessPhoneEntry"
                >
                  + Add number
                </button>
              </div>

              <div class="mt-4 space-y-3">
                <div v-for="(entry, index) in contactForm.phoneEntries" :key="`phone-entry-${index}`" class="rounded-[20px] border border-[#eadfd4] bg-white p-4">
                  <div class="grid gap-3 md:grid-cols-[170px_220px_1fr_auto]">
                    <select v-model="entry.type" class="rounded-2xl border border-[#e8ddd1] bg-white px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                      <option v-for="type in contactPhoneTypes" :key="type.value" :value="type.value">
                        {{ type.label }}
                      </option>
                    </select>
                    <select v-model="entry.phoneCode" class="rounded-2xl border border-[#e8ddd1] bg-white px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                      <option v-for="country in countries" :key="`contact-country-${index}-${country.code}-${country.phoneCode}`" :value="country.phoneCode">
                        {{ country.flag }} {{ country.name }} ({{ country.phoneCode }})
                      </option>
                    </select>
                    <input
                      v-model.trim="entry.number"
                      type="tel"
                      inputmode="numeric"
                      minlength="10"
                      maxlength="10"
                      pattern="[0-9]*"
                      :placeholder="entry.type === 'landline' ? 'e.g. 08045678910' : 'e.g. 9876543210'"
                      class="w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none"
                      @input="syncBusinessPhoneEntry(index)"
                      @blur="syncBusinessPhoneEntry(index)"
                      @keypress="allowOnlyDigitInput"
                      @paste="handleDigitOnlyPaste($event, index)"
                    >
                    <button
                      type="button"
                      class="rounded-2xl border border-[#f0d7d7] bg-white px-4 py-3 text-sm font-semibold text-red-600 transition hover:border-red-300"
                      @click="removeBusinessPhoneEntry(index)"
                    >
                      Remove
                    </button>
                  </div>
                  <p class="mt-3 text-xs font-normal normal-case text-muted">
                    {{ entry.type === 'office' ? 'Saved in office_no.' : entry.type === 'whatsapp' ? 'Saved in whatsapp_no.' : 'Saved in landline_no.' }}
                  </p>
                </div>
                <p v-if="!contactForm.phoneEntries.length" class="rounded-[20px] border border-dashed border-[#eadfd4] bg-white px-4 py-4 text-sm text-muted">
                  No phone numbers added yet.
                </p>
              </div>
              <p v-if="contactFieldErrors.phone" class="mt-3 text-xs font-semibold normal-case text-red-600">{{ contactFieldErrors.phone }}</p>
            </div>
          </section>

          <div v-if="locationPickerOpen" class="fixed inset-0 z-[120] flex items-center justify-center bg-black/55 px-4 py-6">
            <div class="max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-[28px] border border-[#eadfd4] bg-white shadow-2xl">
              <div class="flex flex-wrap items-center justify-between gap-3 border-b border-[#eee4da] px-6 py-5">
                <div>
                  <h4 class="text-[1.45rem] font-semibold text-ink">Choose business location</h4>
                  <p class="mt-1 text-sm text-muted">Search an address, drag the marker, click on the map, or use your current location.</p>
                </div>
                <button type="button" class="rounded-full border border-[#e5d8cc] px-3 py-2 text-sm text-muted transition hover:border-brand hover:text-brand" @click="closeLocationPicker">
                  <i class="ri-close-line text-lg"></i>
                </button>
              </div>

              <div class="grid gap-5 p-6 lg:grid-cols-[320px_minmax(0,1fr)]">
                <div class="space-y-4">
                  <div class="rounded-[22px] border border-[#eadfd4] bg-[#fcf7f2] p-4">
                    <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                      Search location
                      <div class="mt-2 flex gap-2">
                        <input
                          v-model.trim="locationSearchQuery"
                          type="text"
                          placeholder="Search address, area, or landmark"
                          class="w-full rounded-2xl border border-[#e8ddd1] bg-white px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none"
                          @keydown.enter.prevent="searchLocation"
                        >
                        <button
                          type="button"
                          class="rounded-2xl bg-brand px-4 py-3 text-sm font-semibold text-white transition hover:bg-brandDark disabled:opacity-60"
                          :disabled="locationSearchLoading"
                          @click="searchLocation"
                        >
                          {{ locationSearchLoading ? '...' : 'Search' }}
                        </button>
                      </div>
                    </label>
                    <button
                      type="button"
                      class="mt-3 inline-flex items-center gap-2 rounded-2xl border border-[#e5d8cc] bg-white px-4 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand disabled:opacity-60"
                      :disabled="locationCurrentLoading"
                      @click="loadCurrentLocationIntoPicker(true)"
                    >
                      <i class="ri-navigation-line"></i>
                      {{ locationCurrentLoading ? 'Detecting...' : 'Use current location' }}
                    </button>
                    <p v-if="locationPickerError" class="mt-3 text-xs font-semibold normal-case text-red-600">{{ locationPickerError }}</p>
                  </div>

                  <div class="rounded-[22px] border border-[#eadfd4] bg-white p-4">
                    <p class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">Search results</p>
                    <div class="mt-3 max-h-[220px] space-y-2 overflow-y-auto">
                      <button
                        v-for="(item, index) in locationSearchResults"
                        :key="`${item.placeId || item.label}-${index}`"
                        type="button"
                        class="block w-full rounded-2xl border border-[#eadfd4] px-4 py-3 text-left text-sm text-ink transition hover:border-brand hover:bg-[#fbf1e6]"
                        @click="applySearchedLocation(item)"
                      >
                        <span class="block font-semibold">{{ item.label }}</span>
                        <span class="mt-1 block text-xs text-muted">{{ item.meta }}</span>
                      </button>
                      <p v-if="!locationSearchLoading && !locationSearchResults.length" class="rounded-2xl border border-dashed border-[#eadfd4] px-4 py-4 text-sm text-muted">
                        Search for an address or use current location.
                      </p>
                    </div>
                  </div>

                  <div class="rounded-[22px] border border-[#eadfd4] bg-white p-4">
                    <p class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">Selected location</p>
                    <p class="mt-3 text-sm font-medium text-ink">{{ locationPickerAddress || 'No address selected yet' }}</p>
                    <div class="mt-3 grid gap-3 sm:grid-cols-2">
                      <div class="rounded-2xl bg-[#fcf7f2] px-4 py-3 text-sm text-ink">
                        <span class="block text-xs font-semibold uppercase tracking-wide text-[#5b4638]">Latitude</span>
                        <span class="mt-1 block">{{ locationPickerLat || '-' }}</span>
                      </div>
                      <div class="rounded-2xl bg-[#fcf7f2] px-4 py-3 text-sm text-ink">
                        <span class="block text-xs font-semibold uppercase tracking-wide text-[#5b4638]">Longitude</span>
                        <span class="mt-1 block">{{ locationPickerLng || '-' }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="space-y-4">
                  <div id="business-location-picker-map" class="h-[420px] overflow-hidden rounded-[24px] border border-[#eadfd4] bg-[#f5f1eb]"></div>
                  <div class="flex flex-wrap justify-end gap-3">
                    <button type="button" class="rounded-xl border border-[#e5d8cc] bg-white px-5 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand" @click="closeLocationPicker">
                      Cancel
                    </button>
                    <button type="button" class="rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark" @click="confirmLocationPicker">
                      Use this location
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>

        <div v-else-if="activeTab === 'documents'" class="space-y-6">
          <section v-for="slot in documentUploadSections" :key="slot.key" class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
            <h3 class="font-serif text-[2rem] font-semibold text-ink">{{ slot.title }}</h3>
            <p class="mt-2 text-sm text-muted">Only 1 document can be uploaded here. Supported formats: PDF, DOCX, JPG, JPEG, and PNG.</p>
            <label class="mt-4 flex min-h-[118px] cursor-pointer flex-col items-center justify-center rounded-[22px] border border-dashed border-[#e2d2c3] bg-[#fffdfb] px-4 py-6 text-center transition hover:border-brand">
              <input type="file" class="hidden" accept=".jpg,.jpeg,.png,.docx,.pdf" @change="handleDocumentChange($event, slot.key)">
              <i class="ri-upload-cloud-2-line text-3xl text-brand/40"></i>
              <span class="mt-3 text-sm font-medium text-muted">{{ slot.emptyLabel }}</span>
            </label>
            <p v-if="documentErrors[slot.key]" class="mt-2 text-xs font-semibold text-red-600">{{ documentErrors[slot.key] }}</p>
            <div v-if="documentsForm[slot.key]" class="mt-4 rounded-2xl border border-gray-100 bg-white p-3">
              <img
                v-if="getDocumentPreviewType(documentsForm[slot.key]) === 'image'"
                :src="getSelectedDocumentPreview(slot.key)"
                alt="Selected document preview"
                class="h-32 w-full rounded-2xl object-cover"
              >
              <div v-else class="flex items-center gap-3 rounded-2xl border border-gray-100 bg-[#fffaf5] p-3">
                <div class="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-brand ring-1 ring-gray-100">
                  <i :class="getDocumentPreviewIcon(documentsForm[slot.key])"></i>
                </div>
                <div class="min-w-0">
                  <p class="truncate text-sm font-semibold text-ink">{{ documentsForm[slot.key].name }}</p>
                  <p class="mt-1 text-xs font-medium uppercase tracking-wide text-muted">{{ getDocumentExtensionLabel(documentsForm[slot.key]) }}</p>
                </div>
              </div>
              <p class="mt-3 truncate text-sm font-medium text-ink">{{ documentsForm[slot.key].name }}</p>
              <p class="mt-1 text-xs text-muted">{{ formatFileSize(documentsForm[slot.key].size) }}</p>
              <button type="button" class="mt-3 rounded-full border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:border-red-300" @click="removeDocument(slot.key)">Remove</button>
            </div>
            <div v-else-if="existingDocuments[slot.key]" class="mt-4 rounded-2xl border border-gray-100 bg-white p-3">
              <a :href="existingDocuments[slot.key]" target="_blank" rel="noreferrer" class="block">
                <img
                  v-if="getDocumentPreviewType(existingDocuments[slot.key]) === 'image'"
                  :src="existingDocuments[slot.key]"
                  alt="Current document preview"
                  class="h-32 w-full rounded-2xl object-cover"
                >
                <div v-else class="flex items-center gap-3 rounded-2xl border border-gray-100 bg-[#fffaf5] p-3 transition hover:border-brand">
                  <div class="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-brand ring-1 ring-gray-100">
                    <i :class="getDocumentPreviewIcon(existingDocuments[slot.key])"></i>
                  </div>
                  <div class="min-w-0">
                    <p class="truncate text-sm font-semibold text-ink">{{ getDocumentFileName(existingDocuments[slot.key]) }}</p>
                    <p class="mt-1 text-xs font-medium uppercase tracking-wide text-muted">{{ getDocumentExtensionLabel(existingDocuments[slot.key]) }}</p>
                  </div>
                </div>
              </a>
              <button type="button" class="mt-3 rounded-full border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:border-red-300" @click="removeDocument(slot.key)">Remove</button>
            </div>
          </section>

        </div>

        <div v-else-if="activeTab === 'venue'" class="space-y-6">
          <div class="flex flex-wrap items-center justify-between gap-4 rounded-[24px] border border-[#eadfd4] bg-white p-6">
            <div>
              <h3 class="font-serif text-[2rem] font-semibold text-ink">Venue</h3>
              <p class="mt-2 text-sm text-muted">Add and manage venue listings under this business.</p>
            </div>
            <button
              type="button"
              class="rounded-full bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="!isEditMode"
              @click="openModuleEditor('venue')"
            >
              Add venue
            </button>
          </div>

          <div v-if="!isEditMode" class="rounded-[24px] border border-dashed border-[#eadfd4] bg-white p-8 text-sm text-muted">
            Save the business first before adding venues.
          </div>
          <div v-else-if="venueModulesLoading" class="rounded-[24px] border border-dashed border-[#eadfd4] bg-white p-8 text-sm text-muted">
            Loading venues...
          </div>
          <div v-else-if="venueModules.length" class="grid gap-4 md:grid-cols-2">
            <div
              v-for="module in venueModules"
              :key="module.id"
              role="button"
              tabindex="0"
              class="group relative overflow-hidden rounded-[26px] border border-[#eadfd4] bg-[linear-gradient(180deg,#fffdfb_0%,#fff6ef_100%)] p-5 text-left shadow-[0_10px_30px_rgba(102,73,44,0.05)] transition hover:-translate-y-0.5 hover:border-[#d9b594] hover:shadow-[0_18px_40px_rgba(102,73,44,0.12)] focus:outline-none focus:ring-2 focus:ring-[#d9b594]/40"
              @click="openModuleEditor('venue', module.id)"
              @keydown.enter.prevent="openModuleEditor('venue', module.id)"
              @keydown.space.prevent="openModuleEditor('venue', module.id)"
            >
              <div class="pointer-events-none absolute right-0 top-0 h-28 w-28 rounded-full bg-[radial-gradient(circle_at_center,rgba(199,61,61,0.12),rgba(199,61,61,0)_68%)]"></div>
              <div class="relative flex items-start justify-between gap-4">
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-3">
                    <span class="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-brand shadow-sm ring-1 ring-[#eadfd4]">
                      <i class="ri-bank-line text-xl"></i>
                    </span>
                    <div class="min-w-0">
                      <p class="truncate text-[1.3rem] font-semibold leading-tight text-ink">{{ module.name }}</p>
                      <p class="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Venue Listing</p>
                    </div>
                  </div>
                  <p class="mt-4 flex items-start gap-2 text-sm leading-6 text-[#6f5a48]">
                    <i class="ri-map-pin-2-line mt-0.5 text-base text-brand"></i>
                    <span class="line-clamp-2">{{ module.address || 'Address not available' }}</span>
                  </p>
                </div>
                <div class="relative z-10 flex items-start">
                  <button
                    type="button"
                    class="inline-flex h-8 w-8 items-center justify-center rounded-full border border-red-200 bg-white/90 text-red-600 shadow-sm transition hover:border-red-300 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                    :disabled="deletingVenueId === module.id"
                    @click.stop="deleteVenueModule(module)"
                  >
                    <i v-if="deletingVenueId !== module.id" class="ri-delete-bin-line text-sm"></i>
                    <i v-else class="ri-loader-4-line text-sm animate-spin"></i>
                  </button>
                </div>
              </div>

              <div class="relative mt-5 flex flex-wrap items-center gap-2">
                <span class="rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#7b624d] ring-1 ring-[#eadfd4]">
                  {{ module.status || 'Draft' }}
                </span>
                <span class="rounded-full bg-[#f7ebe1] px-3 py-1 text-xs font-semibold text-[#8c674b]">
                  {{ module.spaces ?? 0 }} spaces
                </span>
                <span v-if="module.tagLine" class="truncate rounded-full bg-[#fff3e6] px-3 py-1 text-xs font-medium text-[#a56c43]">
                  {{ module.tagLine }}
                </span>
              </div>

              <div class="relative mt-5 flex items-center justify-between border-t border-[#eadfd4]/80 pt-4">
                <span class="text-sm font-medium text-[#7a6554]">Open venue editor</span>
                <span class="inline-flex items-center gap-2 text-sm font-semibold text-brand transition group-hover:translate-x-0.5">
                  Manage
                  <i class="ri-arrow-right-line"></i>
                </span>
              </div>
            </div>
          </div>
          <div v-else class="rounded-[24px] border border-dashed border-[#eadfd4] bg-white p-8 text-sm text-muted">
            No venues added yet.
          </div>
        </div>

        <div v-else-if="activeTab === 'vendor'" class="space-y-6">
          <div class="flex flex-wrap items-center justify-between gap-4 rounded-[24px] border border-[#eadfd4] bg-white p-6">
            <div>
              <h3 class="font-serif text-[2rem] font-semibold text-ink">Service vendors</h3>
              <p class="mt-2 text-sm text-muted">Add and manage service vendor brands under this business.</p>
            </div>
            <button
              type="button"
              class="rounded-full bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="!isEditMode"
              @click="openModuleEditor('vendor')"
            >
              Add service vendor
            </button>
          </div>

          <div v-if="!isEditMode" class="rounded-[24px] border border-dashed border-[#eadfd4] bg-white p-8 text-sm text-muted">
            Save the business first before adding Service vendors.
          </div>
          <div v-else-if="vendorModules.length" class="grid gap-4 md:grid-cols-2">
            <button
              v-for="module in vendorModules"
              :key="module.id"
              type="button"
              class="rounded-[22px] border border-[#eadfd4] bg-white p-5 text-left transition hover:border-brand hover:shadow-sm"
              @click="openModuleEditor('vendor', module.id)"
            >
              <div class="flex items-start justify-between gap-4">
                <div>
                  <p class="text-[1.65rem] font-semibold text-ink">{{ module.brandName }}</p>
                  <p class="mt-1 text-sm text-muted">{{ module.category || 'No category' }}</p>
                </div>
                <p class="pt-1 text-sm font-medium text-muted">{{ module.progress ?? 0 }}%</p>
              </div>
              <div class="mt-4 flex items-center justify-between">
                <span class="rounded-full bg-[#f5efe8] px-3 py-1 text-sm font-semibold text-[#6d5542]">{{ module.status || 'Draft' }}</span>
              </div>
            </button>
          </div>
          <div v-else class="rounded-[24px] border border-dashed border-[#eadfd4] bg-white p-8 text-sm text-muted">
            No Service vendors added yet.
          </div>
        </div>

        <div v-else-if="activeTab === 'team'" class="space-y-6">
          <section class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
            <h3 class="font-serif text-[2rem] font-semibold text-ink">Team members</h3>
            <p class="mt-2 text-sm text-muted">People who can access and manage this account.</p>
            <div class="mt-5 space-y-4">
              <div v-for="member in teamMembers" :key="member.id" class="grid gap-3 rounded-3xl bg-[#fcf7f2] p-4 md:grid-cols-[1fr_1fr_180px_32px]">
                <input v-model.trim="member.name" type="text" placeholder="Full name" class="rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm focus:border-brand focus:outline-none">
                <input v-model.trim="member.email" type="email" placeholder="Email" class="rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm focus:border-brand focus:outline-none">
                <select v-model="member.role" class="rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm focus:border-brand focus:outline-none">
                  <option>Select...</option>
                  <option>Owner</option>
                  <option>Manager</option>
                  <option>Events Coordinator</option>
                  <option>Finance</option>
                  <option>Staff</option>
                </select>
                <button type="button" class="self-center text-sm font-semibold text-brand transition hover:text-brandDark" @click="removeTeamMember(member.id)">×</button>
              </div>
            </div>
            <button type="button" class="mt-4 rounded-xl border border-[#e5d8cc] bg-white px-5 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand" @click="addTeamMember">
              + Add member
            </button>
          </section>
        </div>

        <div v-else-if="activeTab === 'bank'" class="space-y-6">
          <section class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
            <h3 class="font-serif text-[2rem] font-semibold text-ink">Bank account</h3>
            <p class="mt-2 text-sm text-muted">For future payouts from the platform. Never shown to customers.</p>
            <div class="mt-5 grid gap-4 md:grid-cols-2">
              <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638] md:col-span-2">
                Bank name
                <input v-model.trim="bankForm.bankName" type="text" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                <p v-if="bankFieldErrors.bankName" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ bankFieldErrors.bankName }}</p>
              </label>
              <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                Account number
                <input v-model.trim="bankForm.accountNumber" type="text" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                <p v-if="bankFieldErrors.accountNumber" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ bankFieldErrors.accountNumber }}</p>
              </label>
              <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                IFSC code
                <input v-model.trim="bankForm.ifscCode" type="text" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                <p v-if="bankFieldErrors.ifscCode" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ bankFieldErrors.ifscCode }}</p>
              </label>
              <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638] md:col-span-2">
                UPI id
                <input v-model.trim="bankForm.upiId" type="text" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                <p v-if="bankFieldErrors.upiId" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ bankFieldErrors.upiId }}</p>
              </label>
            </div>
          </section>

        </div>

        <div v-else-if="activeTab === 'menu'" class="space-y-6">
          <div class="rounded-2xl border border-sky-200 bg-sky-50 px-4 py-4 text-sm text-sky-800">
            Define menus once here at the business level. When setting up each venue, choose which menus it offers with optional venue-specific price overrides.
          </div>
          <section class="rounded-[24px] border border-dashed border-brand bg-white p-6">
            <h3 class="font-serif text-[2rem] font-semibold text-brand">New menu</h3>
            <div class="mt-5 grid gap-4 md:grid-cols-2">
              <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                Menu title <span class="text-brand">*</span>
                <input v-model.trim="menuForm.title" type="text" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              </label>
              <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                Type
                <select v-model="menuForm.type" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                  <option value="">Select...</option>
                  <option>Veg Standard</option>
                  <option>Veg Premium</option>
                  <option>Non-Veg Standard</option>
                  <option>Non-Veg Premium</option>
                  <option>Jain</option>
                  <option>Vegan</option>
                  <option>Mixed / Both</option>
                  <option>Custom</option>
                </select>
              </label>
              <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                Price / plate (₹)
                <input v-model.trim="menuForm.pricePerPlate" type="text" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              </label>
              <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                Min plates
                <input v-model.trim="menuForm.minPlates" type="text" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              </label>
            </div>
            <div class="mt-5 flex flex-wrap gap-3">
              <button type="button" class="rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark">Save menu</button>
              <button type="button" class="rounded-xl border border-[#e5d8cc] bg-white px-5 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand">Cancel</button>
            </div>
          </section>
        </div>

        <div v-if="showTabStepper" class="mt-8 flex flex-wrap items-center justify-between gap-3 rounded-[24px] border border-[#eadfd4] bg-white px-5 py-4">
          <button
            v-if="previousTabItem"
            type="button"
            class="inline-flex items-center gap-2 rounded-xl border border-[#e5d8cc] bg-white px-5 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand"
            @click="goToPreviousTab"
          >
            <i class="ri-arrow-left-line"></i>
            Previous
          </button>
          <span v-else></span>

          <div class="flex flex-wrap items-center gap-3">
            <button
              v-if="isActiveTabSaveable"
              type="button"
              class="rounded-xl border border-[#e5d8cc] bg-white px-5 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="saving"
              @click="saveTabOnly"
            >
              {{ saving ? 'Saving...' : saveTabButtonLabel }}
            </button>

            <button
              v-if="nextTabItem"
              type="button"
              class="inline-flex items-center gap-2 rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="saving"
              @click="goToNextTab"
            >
              {{ saving ? 'Saving...' : 'Next' }}
              <i class="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>

        <p v-if="formError" class="mt-6 text-sm font-semibold text-red-600">{{ formError }}</p>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import markerIcon from '@/assets/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'
import Swal from 'sweetalert2'
import api from '@/services/vendor/api'
import { getAuthUserId } from '@/services/auth'
import countries from '@/data/countries.json'

const route = useRoute()
const router = useRouter()

const activeTab = ref('business')
const loading = ref(false)
const saving = ref(false)
const loadError = ref('')
const formError = ref('')
const businessImagePreview = ref('')
const defaultCountryPhoneCode = '+91'

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon,
  iconUrl: markerIcon,
  shadowUrl: markerShadow
})

const showSuccessToast = async (title) => {
  await Swal.fire({
    toast: true,
    position: 'top-end',
    icon: 'success',
    title,
    showConfirmButton: false,
    timer: 1800,
    timerProgressBar: true
  })
}

const showErrorToast = async (title) => {
  await Swal.fire({
    toast: true,
    position: 'top-end',
    icon: 'error',
    title,
    showConfirmButton: false,
    timer: 2400,
    timerProgressBar: true
  })
}

const tabs = [
  { id: 'business', label: 'Business info' },
  { id: 'contact', label: 'contact & Address details' },
  { id: 'documents', label: 'Documents' },
  { id: 'venue', label: 'Venue' },
  { id: 'vendor', label: 'Service vendor' },
  { id: 'bank', label: 'Bank & payouts' }
]
const saveableTabs = ['business', 'contact', 'documents', 'bank']

const documentUploadSections = [
  { key: 'doc1', title: 'Trade license', emptyLabel: 'Upload trade license' },
  { key: 'doc2', title: 'Insurance certificate', emptyLabel: 'Upload insurance certificate' },
  { key: 'doc3', title: 'Other certifications', emptyLabel: 'Upload other certifications' }
]

const businessForm = reactive({
  businessName: '',
  ownerName: '',
  description: '',
  imageFile: null,
  imageName: '',
  existingImage: ''
})

const businessMeta = reactive({
  entityType: '',
  gstNumber: '',
  pan: '',
  tradeLicenseNumber: '',
  city: '',
  state: '',
  pinCode: ''
})

const contactPhoneTypes = [
  { value: 'office', label: 'Office' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'landline', label: 'Landline' }
]

const contactForm = reactive({
  email: '',
  website: '',
  contactPersonName: '',
  address: '',
  latitude: '',
  longitude: '',
  currency: '',
  zone: '',
  timezone: '',
  country: '',
  state: '',
  city: '',
  phoneEntries: [{ type: 'office', phoneCode: defaultCountryPhoneCode, number: '' }]
})

const locationPickerOpen = ref(false)
const locationSearchQuery = ref('')
const locationSearchResults = ref([])
const locationSearchLoading = ref(false)
const locationCurrentLoading = ref(false)
const locationPickerError = ref('')
const locationPickerAddress = ref('')
const locationPickerLat = ref('')
const locationPickerLng = ref('')
const locationMap = ref(null)
const locationMarker = ref(null)

const teamMembers = reactive([
  { id: 1, name: '', email: '', role: 'Owner' }
])

const bankForm = reactive({
  bankName: '',
  accountNumber: '',
  ifscCode: '',
  upiId: ''
})

const menuForm = reactive({
  title: '',
  type: '',
  pricePerPlate: '',
  minPlates: ''
})

const documentsForm = reactive({
  doc1: null,
  doc2: null,
  doc3: null
})

const documentPreviews = reactive({
  doc1: '',
  doc2: '',
  doc3: ''
})

const existingDocuments = reactive({
  doc1: '',
  doc2: '',
  doc3: ''
})

const businessErrors = reactive({
  image: ''
})

const businessFieldErrors = reactive({
  businessName: '',
  entityType: '',
  gstNumber: '',
  pan: '',
  tradeLicenseNumber: '',
  ownerName: '',
  description: '',
  city: '',
  pinCode: ''
})

const contactFieldErrors = reactive({
  email: '',
  phone: '',
  website: ''
})

const bankFieldErrors = reactive({
  bankName: '',
  accountNumber: '',
  ifscCode: '',
  upiId: ''
})

const documentErrors = reactive({
  doc1: '',
  doc2: '',
  doc3: ''
})

const businessId = computed(() => route.params.id || '')
const isEditMode = computed(() => Boolean(businessId.value))
const businessLabel = computed(() => businessForm.businessName || 'Edit business')
const ALLOWED_DOCUMENT_EXTENSIONS = ['jpg', 'jpeg', 'png', 'docx', 'pdf']
const MAX_DOCUMENT_SIZE = 5 * 1024 * 1024
const venueModules = ref([])
const vendorModules = ref([])
const venueModulesLoading = ref(false)
const deletingVenueId = ref('')

const getModuleStorageKey = (type) => `business_${businessId.value}_${type}_modules`

const readStoredModules = (type) => {
  if (typeof window === 'undefined' || !businessId.value) return []
  try {
    const parsed = JSON.parse(window.localStorage.getItem(getModuleStorageKey(type)) || '[]')
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const writeStoredModules = (type, items) => {
  if (typeof window === 'undefined' || !businessId.value) return
  window.localStorage.setItem(getModuleStorageKey(type), JSON.stringify(Array.isArray(items) ? items : []))
}

const normalizeVenueModule = (item, index) => ({
  id: item?.id || item?.venue_id || item?.vendor_business_venue_id || `venue-${index}`,
  venueId: item?.venue_id || item?.id || item?.vendor_business_venue_id || '',
  name: item?.title || item?.name || 'Untitled venue',
  address: item?.address || item?.location || '',
  tagLine: item?.tag_line || item?.tagLine || '',
  spaces: item?.spaces ?? item?.space_count ?? item?.total_spaces ?? 0,
  status: String(item?.status || '').trim() === '1' ? 'Active' : item?.status || 'Draft',
  progress: item?.progress ?? 0
})

const fetchVenueModules = async () => {
  if (!businessId.value) {
    venueModules.value = []
    return
  }
  venueModulesLoading.value = true
  try {
    const formData = new FormData()
    formData.append('venue_id', businessId.value)
    formData.append('business_id', businessId.value)
    const { data } = await api.listVenuesBusinessVendor(formData)
    const payload = data?.data || data || {}
    const collection = Array.isArray(payload)
      ? payload
      : payload?.venues || payload?.items || payload?.list || payload?.venue_list || []
    venueModules.value = Array.isArray(collection) ? collection.map(normalizeVenueModule) : []
  } catch {
    venueModules.value = readStoredModules('venue')
  } finally {
    venueModulesLoading.value = false
  }
}

const refreshModules = async () => {
  await fetchVenueModules()
  vendorModules.value = readStoredModules('vendor')
}

const currentTabIndex = computed(() => tabs.findIndex((item) => item.id === activeTab.value))
const previousTabItem = computed(() => tabs[currentTabIndex.value - 1] || null)
const nextTabItem = computed(() => tabs[currentTabIndex.value + 1] || null)
const isActiveTabSaveable = computed(() => saveableTabs.includes(activeTab.value))
const showTabStepper = computed(() => Boolean(previousTabItem.value || nextTabItem.value))
const saveTabButtonLabel = computed(() => {
  if (activeTab.value === 'business') return isEditMode.value ? 'Save business info' : 'Create business'
  if (activeTab.value === 'contact') return 'Save contact & Address details'
  if (activeTab.value === 'documents') return 'Save documents'
  if (activeTab.value === 'bank') return 'Save bank details'
  return 'Save'
})

const setTab = async (tabId) => {
  activeTab.value = tabId
  await router.replace({
    path: route.path,
    query: {
      ...route.query,
      tab: tabId
    }
  })
}

const finalizeBusinessTabNavigation = async (targetTabId, saveResult) => {
  if (activeTab.value === 'business' && saveResult?.businessId) {
    await router.replace({
      name: 'business-edit',
      params: { id: saveResult.businessId },
      query: {
        ...route.query,
        tab: targetTabId
      }
    })
    await loadBusiness()
    await refreshModules()
    return
  }
  await setTab(targetTabId)
}

const goToPreviousTab = async () => {
  if (!previousTabItem.value || saving.value) return
  await setTab(previousTabItem.value.id)
}

const saveTabOnly = async () => {
  if (!isActiveTabSaveable.value || saving.value) return
  const savedTabLabel = tabs.find((item) => item.id === activeTab.value)?.label || 'Tab'
  formError.value = ''
  saving.value = true
  try {
    const saveResult = await saveCurrentTab()
    if (saveResult === false) return
    if (activeTab.value === 'business' && saveResult?.businessId) {
      await finalizeBusinessTabNavigation(activeTab.value, saveResult)
    }
    await showSuccessToast(`${savedTabLabel} saved successfully`)
  } catch (error) {
    formError.value = error?.response?.data?.msg || error?.response?.data?.message || error?.message || 'Failed to save business'
    await showErrorToast(formError.value)
  } finally {
    saving.value = false
  }
}

const goToNextTab = async () => {
  if (!nextTabItem.value || saving.value) return
  if (!isActiveTabSaveable.value) {
    await setTab(nextTabItem.value.id)
    return
  }

  const savedTabLabel = tabs.find((item) => item.id === activeTab.value)?.label || 'Tab'
  formError.value = ''
  saving.value = true
  try {
    const saveResult = await saveCurrentTab()
    if (saveResult === false) return
    await finalizeBusinessTabNavigation(nextTabItem.value.id, saveResult)
    await showSuccessToast(`${savedTabLabel} saved successfully`)
  } catch (error) {
    formError.value = error?.response?.data?.msg || error?.response?.data?.message || error?.message || 'Failed to save business'
    await showErrorToast(formError.value)
  } finally {
    saving.value = false
  }
}

const formatFileSize = (size = 0) => {
  if (!size) return '0 B'
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`
  if (size >= 1024) return `${Math.round(size / 1024)} KB`
  return `${size} B`
}

const isValidExistingDocument = (value) => {
  if (value == null) return false
  const normalized = String(value).trim()
  return normalized !== '' && normalized !== '0'
}

const getDocumentExtension = (value) => {
  if (!isValidExistingDocument(value)) return ''
  if (value instanceof File) {
    return (value.name.split('.').pop() || '').toLowerCase()
  }
  const cleanedValue = String(value).split('?')[0].split('#')[0]
  const filename = cleanedValue.split('/').pop() || ''
  return (filename.split('.').pop() || '').toLowerCase()
}

const getDocumentPreviewType = (value) => ['jpg', 'jpeg', 'png'].includes(getDocumentExtension(value)) ? 'image' : 'file'
const getDocumentFileName = (value) => {
  if (value instanceof File) return value.name || 'Selected document'
  return String(value).split('?')[0].split('#')[0].split('/').pop() || 'Current document'
}
const getDocumentExtensionLabel = (value) => {
  const extension = getDocumentExtension(value)
  if (extension === 'pdf') return 'PDF'
  if (extension === 'doc' || extension === 'docx') return extension.toUpperCase()
  if (extension === 'jpg' || extension === 'jpeg' || extension === 'png') return 'IMAGE'
  return extension ? extension.toUpperCase() : 'FILE'
}
const getDocumentPreviewIcon = (value) => {
  const extension = getDocumentExtension(value)
  if (extension === 'pdf') return 'ri-file-pdf-line'
  if (extension === 'doc' || extension === 'docx') return 'ri-file-word-line'
  if (extension === 'jpg' || extension === 'jpeg' || extension === 'png') return 'ri-image-line'
  return 'ri-file-line'
}

const getSelectedDocumentPreview = (slotKey) => documentPreviews[slotKey] || ''
const hasExistingUploadedDocuments = () => Object.values(existingDocuments).some((value) => isValidExistingDocument(value))

const resetDocumentErrors = () => {
  documentErrors.doc1 = ''
  documentErrors.doc2 = ''
  documentErrors.doc3 = ''
}

const resetBusinessFieldErrors = () => {
  businessFieldErrors.businessName = ''
  businessFieldErrors.entityType = ''
  businessFieldErrors.gstNumber = ''
  businessFieldErrors.pan = ''
  businessFieldErrors.tradeLicenseNumber = ''
  businessFieldErrors.ownerName = ''
  businessFieldErrors.description = ''
  businessFieldErrors.city = ''
  businessFieldErrors.pinCode = ''
}

const resetContactFieldErrors = () => {
  contactFieldErrors.email = ''
  contactFieldErrors.phone = ''
  contactFieldErrors.website = ''
}

const resetBankFieldErrors = () => {
  bankFieldErrors.bankName = ''
  bankFieldErrors.accountNumber = ''
  bankFieldErrors.ifscCode = ''
  bankFieldErrors.upiId = ''
}

const isValidEmail = (value) => !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
const isValidUrl = (value) => !value || /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/i.test(value)
const isValidIfsc = (value) => !value || /^[A-Z]{4}0[A-Z0-9]{6}$/i.test(value)
const isValidUpiId = (value) => !value || /^[\w.-]{2,256}@[a-zA-Z]{2,64}$/.test(value)
const isValidPhoneDigits = (value) => {
  const digits = String(value || '').replace(/\D/g, '')
  return digits.length === 10
}
const splitCommaValues = (value) => {
  const normalized = String(value || '').split(',').map((entry) => entry.trim()).filter(Boolean)
  return normalized.length ? normalized : ['']
}
const createPhoneEntry = (phoneCode = defaultCountryPhoneCode, number = '') => ({
  phoneCode: normalizeCountryPhoneCode(phoneCode),
  number: String(number || '').replace(/\D/g, '').slice(-10)
})
const createBusinessPhoneEntry = (type = 'office', phoneCode = defaultCountryPhoneCode, number = '') => ({
  type: ['office', 'whatsapp', 'landline'].includes(type) ? type : 'office',
  phoneCode: normalizeCountryPhoneCode(phoneCode),
  number: String(number || '').replace(/\D/g, '').slice(-10)
})
const splitPhoneEntries = (value) => {
  const parts = String(value || '').split(',').map((entry) => entry.trim()).filter(Boolean)
  if (!parts.length) return []
  return parts.map((entry) => {
    const matchedCountry = countries
      .slice()
      .sort((a, b) => b.phoneCode.length - a.phoneCode.length)
      .find((country) => entry.startsWith(country.phoneCode))
    if (!matchedCountry) return createPhoneEntry(defaultCountryPhoneCode, String(entry || '').replace(/\D/g, '').slice(-10))
    const localNumber = entry.slice(matchedCountry.phoneCode.length)
    return isValidPhoneDigits(localNumber)
      ? createPhoneEntry(matchedCountry.phoneCode, localNumber)
      : createPhoneEntry(matchedCountry.phoneCode, String(localNumber || entry).replace(/\D/g, '').slice(-10))
  }).filter((entry) => isValidPhoneDigits(entry.number))
}
const normalizeCountryPhoneCode = (value) => {
  const normalized = String(value || '').trim()
  if (!normalized) return defaultCountryPhoneCode
  return countries.some((country) => country.phoneCode === normalized) ? normalized : defaultCountryPhoneCode
}
const splitPhoneWithCode = (phoneValue, phoneCodeValue) => {
  const digits = String(phoneValue || '').replace(/\D/g, '')
  const normalizedCode = normalizeCountryPhoneCode(phoneCodeValue)
  const codeDigits = normalizedCode.replace(/\D/g, '')
  if (!digits) return { phoneCode: normalizedCode, number: '' }
  if (codeDigits && digits.startsWith(codeDigits)) {
    const localNumber = digits.slice(codeDigits.length)
    if (isValidPhoneDigits(localNumber)) {
      return { phoneCode: normalizedCode, number: localNumber }
    }
  }
  return { phoneCode: normalizedCode, number: digits.slice(-10) }
}
const buildBusinessPhoneEntries = (item = {}) => {
  const entries = [
    ...splitPhoneEntries(item.office_no ?? item.officeNo ?? '').map((entry) => createBusinessPhoneEntry('office', entry.phoneCode, entry.number)),
    ...splitPhoneEntries(item.whatsapp_no ?? item.whatsappNo ?? '').map((entry) => createBusinessPhoneEntry('whatsapp', entry.phoneCode, entry.number)),
    ...splitCommaValues(item.landline_no ?? item.landlineNo ?? '')
      .filter(Boolean)
      .map((entry) => createBusinessPhoneEntry('landline', defaultCountryPhoneCode, String(entry || '').replace(/\D/g, '').slice(-10)))
  ]
  const fallbackPhone = String(item.phone ?? '').trim()
  if (!entries.length && fallbackPhone) {
    const fallbackEntry = splitPhoneWithCode(fallbackPhone, item.phone_code ?? item.phoneCode ?? defaultCountryPhoneCode)
    if (isValidPhoneDigits(fallbackEntry.number)) {
      entries.push(createBusinessPhoneEntry('office', fallbackEntry.phoneCode, fallbackEntry.number))
    }
  }
  return entries.filter((entry) => isValidPhoneDigits(entry.number))
}
const getPhonePayloadByType = () => {
  const payload = {
    office: [],
    whatsapp: [],
    landline: []
  }
  contactForm.phoneEntries.forEach((entry) => {
    const digits = String(entry?.number || '').replace(/\D/g, '').slice(0, 10)
    if (!digits) return
    if (entry.type === 'landline') {
      payload.landline.push(digits)
      return
    }
    payload[entry.type].push(`${normalizeCountryPhoneCode(entry?.phoneCode)}${digits}`)
  })
  return payload
}
const getPrimaryPhoneEntry = () => contactForm.phoneEntries.find((entry) => isValidPhoneDigits(entry?.number)) || null

const formatCoordinate = (value) => {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric.toFixed(6) : ''
}

const countryCurrencyCache = new Map()

const setContactLocationFields = ({
  address = '',
  latitude = '',
  longitude = '',
  currency = contactForm.currency,
  zone = contactForm.zone,
  timezone = contactForm.timezone,
  country = contactForm.country,
  state = contactForm.state,
  city = contactForm.city
} = {}) => {
  contactForm.address = String(address || '').trim()
  contactForm.latitude = formatCoordinate(latitude)
  contactForm.longitude = formatCoordinate(longitude)
  contactForm.currency = String(currency || '').trim()
  contactForm.zone = String(zone || '').trim()
  contactForm.timezone = String(timezone || '').trim()
  contactForm.country = String(country || '').trim()
  contactForm.state = String(state || '').trim()
  contactForm.city = String(city || '').trim()
}

const setLocationPickerSelection = ({ address = '', latitude = '', longitude = '' } = {}) => {
  locationPickerAddress.value = String(address || '').trim()
  locationPickerLat.value = formatCoordinate(latitude)
  locationPickerLng.value = formatCoordinate(longitude)
}

const fetchCurrencyByCountryCode = async (countryCode) => {
  const normalizedCode = String(countryCode || '').trim().toUpperCase()
  if (!normalizedCode) return ''
  if (countryCurrencyCache.has(normalizedCode)) return countryCurrencyCache.get(normalizedCode)
  try {
    const response = await fetch(`https://restcountries.com/v3.1/alpha/${encodeURIComponent(normalizedCode)}?fields=currencies`, {
      headers: {
        Accept: 'application/json'
      }
    })
    const payload = await response.json()
    const currencies = Array.isArray(payload) ? payload[0]?.currencies : payload?.currencies
    const resolved = currencies ? Object.keys(currencies)[0] || '' : ''
    countryCurrencyCache.set(normalizedCode, resolved)
    return resolved
  } catch {
    countryCurrencyCache.set(normalizedCode, '')
    return ''
  }
}

const fetchTimezoneMetadata = async (latitude, longitude) => {
  try {
    const response = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${encodeURIComponent(latitude)}&longitude=${encodeURIComponent(longitude)}&current=temperature_2m&timezone=auto`, {
      headers: {
        Accept: 'application/json'
      }
    })
    const payload = await response.json()
    const offsetSeconds = Number(payload?.utc_offset_seconds ?? 0)
    const offsetSign = offsetSeconds >= 0 ? '+' : '-'
    const offsetHours = String(Math.floor(Math.abs(offsetSeconds) / 3600))
    const offsetMinutes = String(Math.floor((Math.abs(offsetSeconds) % 3600) / 60)).padStart(2, '0')
    return {
      timezone: payload?.timezone || '',
      zone: `GMT${offsetSign}${offsetHours}:${offsetMinutes}`
    }
  } catch {
    return {
      timezone: '',
      zone: ''
    }
  }
}

const ensureLocationMarker = (lat, lng, updateView = true) => {
  if (!locationMap.value) return
  if (!locationMarker.value) {
    locationMarker.value = L.marker([lat, lng], { draggable: true }).addTo(locationMap.value)
    locationMarker.value.on('dragend', async (event) => {
      const nextLatLng = event.target.getLatLng()
      await reverseGeocodeLocation(nextLatLng.lat, nextLatLng.lng, true)
    })
  } else {
    locationMarker.value.setLatLng([lat, lng])
  }
  if (updateView) {
    locationMap.value.setView([lat, lng], Math.max(locationMap.value.getZoom(), 15))
  }
}

const reverseGeocodeLocation = async (latitude, longitude, syncMap = false) => {
  locationPickerError.value = ''
  try {
    const [geoResponse, timezoneMeta] = await Promise.all([
      fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(latitude)}&lon=${encodeURIComponent(longitude)}`, {
        headers: {
          Accept: 'application/json'
        }
      }),
      fetchTimezoneMetadata(latitude, longitude)
    ])
    const payload = await geoResponse.json()
    const addressParts = payload?.address || {}
    const countryCode = String(addressParts?.country_code || '').toUpperCase()
    const currency = await fetchCurrencyByCountryCode(countryCode)
    setContactLocationFields({
      address: payload?.display_name || '',
      latitude,
      longitude,
      currency,
      zone: timezoneMeta.zone || addressParts?.state_district || addressParts?.county || addressParts?.region || '',
      timezone: timezoneMeta.timezone,
      country: addressParts?.country || '',
      state: addressParts?.state || '',
      city: addressParts?.city || addressParts?.town || addressParts?.village || addressParts?.municipality || ''
    })
    setLocationPickerSelection({
      address: payload?.display_name || '',
      latitude,
      longitude
    })
    if (syncMap) ensureLocationMarker(Number(latitude), Number(longitude), false)
  } catch {
    setContactLocationFields({
      address: '',
      latitude,
      longitude
    })
    setLocationPickerSelection({
      address: '',
      latitude,
      longitude
    })
    locationPickerError.value = 'Unable to fetch address for this point. You can still use the selected coordinates.'
  }
}

const searchLocation = async () => {
  const query = locationSearchQuery.value.trim()
  if (query.length < 3) {
    locationSearchResults.value = []
    locationPickerError.value = 'Enter at least 3 characters to search.'
    return
  }
  locationPickerError.value = ''
  locationSearchLoading.value = true
  try {
    const response = await fetch(`https://nominatim.openstreetmap.org/search?format=jsonv2&limit=6&q=${encodeURIComponent(query)}`, {
      headers: {
        Accept: 'application/json'
      }
    })
    const payload = await response.json()
    locationSearchResults.value = Array.isArray(payload)
      ? payload.map((item) => ({
          placeId: item?.place_id || '',
          label: item?.display_name || query,
          meta: `${Number(item?.lat).toFixed(6)}, ${Number(item?.lon).toFixed(6)}`,
          latitude: item?.lat,
          longitude: item?.lon
        }))
      : []
    if (!locationSearchResults.value.length) {
      locationPickerError.value = 'No matching locations found.'
    }
  } catch {
    locationSearchResults.value = []
    locationPickerError.value = 'Unable to search locations right now.'
  } finally {
    locationSearchLoading.value = false
  }
}

const applySearchedLocation = async (item) => {
  if (!item?.latitude || !item?.longitude) return
  locationSearchQuery.value = item.label || ''
  setLocationPickerSelection({
    address: item.label || '',
    latitude: item.latitude,
    longitude: item.longitude
  })
  ensureLocationMarker(Number(item.latitude), Number(item.longitude), true)
  await reverseGeocodeLocation(item.latitude, item.longitude, false)
}

const ensureLocationMap = async () => {
  await nextTick()
  if (!locationMap.value) {
    locationMap.value = L.map('business-location-picker-map').setView([20.5937, 78.9629], 5)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(locationMap.value)
    locationMap.value.on('click', async (event) => {
      const { lat, lng } = event.latlng
      ensureLocationMarker(lat, lng, false)
      await reverseGeocodeLocation(lat, lng, false)
    })
  }

  const initialLat = Number(locationPickerLat.value || contactForm.latitude || 20.5937)
  const initialLng = Number(locationPickerLng.value || contactForm.longitude || 78.9629)
  if (Number.isFinite(initialLat) && Number.isFinite(initialLng)) {
    ensureLocationMarker(initialLat, initialLng, true)
  }
  locationMap.value.invalidateSize()
}

const loadCurrentLocationIntoPicker = async (forceUpdate = false) => {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    locationPickerError.value = 'Geolocation is not supported in this browser.'
    return
  }
  if (!forceUpdate && locationPickerLat.value && locationPickerLng.value) return
  locationPickerError.value = ''
  locationCurrentLoading.value = true
  try {
    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000
      })
    })
    const latitude = position.coords.latitude
    const longitude = position.coords.longitude
    ensureLocationMarker(latitude, longitude, true)
    await reverseGeocodeLocation(latitude, longitude, false)
  } catch {
    locationPickerError.value = 'Unable to detect your current location. Search manually or place the pin on the map.'
  } finally {
    locationCurrentLoading.value = false
  }
}

const openLocationPicker = async () => {
  locationPickerOpen.value = true
  locationPickerError.value = ''
  locationSearchResults.value = []
  locationSearchQuery.value = contactForm.address || ''
  setLocationPickerSelection({
    address: contactForm.address,
    latitude: contactForm.latitude,
    longitude: contactForm.longitude
  })
  await ensureLocationMap()
  if (!contactForm.latitude || !contactForm.longitude) {
    await loadCurrentLocationIntoPicker()
  } else {
    ensureLocationMarker(Number(contactForm.latitude), Number(contactForm.longitude), true)
  }
}

const closeLocationPicker = () => {
  locationPickerOpen.value = false
  locationPickerError.value = ''
  if (locationMap.value) {
    locationMap.value.remove()
    locationMap.value = null
  }
  locationMarker.value = null
}

const confirmLocationPicker = () => {
  setContactLocationFields({
    address: locationPickerAddress.value || contactForm.address,
    latitude: locationPickerLat.value || contactForm.latitude,
    longitude: locationPickerLng.value || contactForm.longitude
  })
  closeLocationPicker()
}

const bootstrapContactLocation = async () => {
  if (contactForm.latitude && contactForm.longitude) return
  if (typeof navigator === 'undefined' || !navigator.geolocation) return
  try {
    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000
      })
    })
    const latitude = position.coords.latitude
    const longitude = position.coords.longitude
    await reverseGeocodeLocation(latitude, longitude, false)
    setContactLocationFields({
      address: locationPickerAddress.value,
      latitude,
      longitude
    })
  } catch {
    // Leave blank when permission is denied.
  }
}

const validateBusinessTab = () => {
  resetBusinessFieldErrors()
  businessErrors.image = ''
  let hasError = false
  const hasBusinessImage = Boolean(
    businessForm.imageFile ||
    String(businessForm.existingImage || '').trim() ||
    String(businessImagePreview.value || '').trim()
  )

  if (!businessForm.businessName) {
    businessFieldErrors.businessName = 'Business name is required.'
    hasError = true
  }
  if (!businessMeta.entityType) {
    businessFieldErrors.entityType = 'Entity type is required.'
    hasError = true
  }
  if (!businessMeta.gstNumber) {
    businessFieldErrors.gstNumber = 'GST number is required.'
    hasError = true
  }
  if (!businessMeta.pan) {
    businessFieldErrors.pan = 'PAN is required.'
    hasError = true
  }
  if (!businessMeta.tradeLicenseNumber) {
    businessFieldErrors.tradeLicenseNumber = 'Trade license number is required.'
    hasError = true
  }
  if (!businessForm.ownerName) {
    businessFieldErrors.ownerName = 'Owner name is required.'
    hasError = true
  }
  if (!businessForm.description) {
    businessFieldErrors.description = 'Description is required.'
    hasError = true
  }
  if (!hasBusinessImage) {
    businessErrors.image = 'Business image is required.'
    hasError = true
  }

  return !hasError
}

const validateContactTab = () => {
  resetContactFieldErrors()
  let hasError = false

  if (!contactForm.email) {
    contactFieldErrors.email = 'Email is required.'
    hasError = true
  } else if (!isValidEmail(contactForm.email)) {
    contactFieldErrors.email = 'Enter a valid email address.'
    hasError = true
  }
  const hasValidPhone = contactForm.phoneEntries.some((entry) => isValidPhoneDigits(entry?.number))
  if (!hasValidPhone) {
    contactFieldErrors.phone = 'Add at least one valid 10-digit phone number.'
    hasError = true
  }
  if (!contactForm.website) {
    contactFieldErrors.website = 'Website is required.'
    hasError = true
  } else if (!isValidUrl(contactForm.website)) {
    contactFieldErrors.website = 'Enter a valid website URL.'
    hasError = true
  }

  return !hasError
}

const validateBankTab = () => {
  resetBankFieldErrors()
  let hasError = false

  if (!bankForm.bankName) {
    bankFieldErrors.bankName = 'Bank name is required.'
    hasError = true
  }
  if (!bankForm.accountNumber) {
    bankFieldErrors.accountNumber = 'Account number is required.'
    hasError = true
  } else if (!/^\d{9,18}$/.test(bankForm.accountNumber)) {
    bankFieldErrors.accountNumber = 'Account number must be 9 to 18 digits.'
    hasError = true
  }
  if (!bankForm.ifscCode) {
    bankFieldErrors.ifscCode = 'IFSC code is required.'
    hasError = true
  } else if (!isValidIfsc(bankForm.ifscCode)) {
    bankFieldErrors.ifscCode = 'Enter a valid IFSC code.'
    hasError = true
  }
  if (!isValidUpiId(bankForm.upiId)) {
    bankFieldErrors.upiId = 'Enter a valid UPI id.'
    hasError = true
  }

  return !hasError
}

const revokeDocumentPreview = (slotKey) => {
  if (documentPreviews[slotKey] && documentPreviews[slotKey].startsWith('blob:')) {
    URL.revokeObjectURL(documentPreviews[slotKey])
  }
  documentPreviews[slotKey] = ''
}

const revokeAllDocumentPreviews = () => {
  revokeDocumentPreview('doc1')
  revokeDocumentPreview('doc2')
  revokeDocumentPreview('doc3')
}

const revokeBusinessImagePreview = () => {
  if (businessImagePreview.value && businessImagePreview.value.startsWith('blob:')) {
    URL.revokeObjectURL(businessImagePreview.value)
  }
}

const populateFromBusiness = (item = {}) => {
  businessForm.businessName = item.business_name || item.businessName || item.name || ''
  businessForm.ownerName = item.owner_name || item.ownerName || item.owner || ''
  businessForm.description = item.description || ''
  businessForm.existingImage = item.photo || item.image || item.img || ''
  businessForm.imageName = businessForm.existingImage ? 'Current image' : ''
  revokeBusinessImagePreview()
  businessImagePreview.value = businessForm.existingImage || ''

  contactForm.email = item.email ?? ''
  contactForm.website = item.website ?? ''
  contactForm.contactPersonName = String(item.contact_person_name ?? item.contactPersonName ?? '').split(',')[0]?.trim() || ''
  contactForm.address = item.address ?? item.street_address ?? item.streetAddress ?? ''
  contactForm.latitude = formatCoordinate(item.latitude ?? item.lat ?? '')
  contactForm.longitude = formatCoordinate(item.longitude ?? item.long ?? item.lng ?? '')
  contactForm.currency = item.currency ?? ''
  contactForm.zone = item.zone ?? ''
  contactForm.timezone = item.timezone ?? ''
  contactForm.country = item.country ?? ''
  contactForm.state = item.state ?? ''
  contactForm.city = item.city ?? ''
  contactForm.phoneEntries = buildBusinessPhoneEntries(item)

  businessMeta.entityType = item.entity_type || item.entityType || ''
  businessMeta.gstNumber = item.gst_number || item.gstNumber || ''
  businessMeta.pan = item.pan_card_no || item.pan || ''
  businessMeta.tradeLicenseNumber = item.trade_license_no || item.trade_license_number || item.tradeLicenseNumber || ''
  businessMeta.city = item.city || ''
  businessMeta.state = item.state || ''
  businessMeta.pinCode = item.pin_code || item.pinCode || ''

  bankForm.bankName = item.bank_name || item.bankName || ''
  bankForm.accountNumber = item.account_no || item.accountNumber || ''
  bankForm.ifscCode = item.ifsc_code || item.ifscCode || ''
  bankForm.upiId = item.upi_id || item.upiId || ''

  existingDocuments.doc1 = isValidExistingDocument(item.doc1) ? item.doc1 : ''
  existingDocuments.doc2 = isValidExistingDocument(item.doc2) ? item.doc2 : ''
  existingDocuments.doc3 = isValidExistingDocument(item.doc3) ? item.doc3 : ''

}

const syncBusinessPhoneEntry = (index) => {
  const item = contactForm.phoneEntries?.[index]
  if (!item) return
  item.number = String(item.number || '').replace(/\D/g, '').slice(0, 10)
  item.phoneCode = normalizeCountryPhoneCode(item.phoneCode)
}

const allowOnlyDigitInput = (event) => {
  if (!/^\d$/.test(event.key)) {
    event.preventDefault()
  }
}

const handleDigitOnlyPaste = (event, index) => {
  event.preventDefault()
  const pasted = event.clipboardData?.getData('text') || ''
  const digits = pasted.replace(/\D/g, '').slice(-10)
  const item = contactForm.phoneEntries?.[index]
  if (!item) return
  item.number = digits
}

const appendBusinessInfoFields = (formData) => {
  formData.append('business_name', businessForm.businessName)
  formData.append('owner_name', businessForm.ownerName)
  formData.append('description', businessForm.description)
  formData.append('entity_type', businessMeta.entityType || '')
  formData.append('gst_number', businessMeta.gstNumber || '')
  formData.append('pan_card_no', businessMeta.pan || '')
  formData.append('trade_license_no', businessMeta.tradeLicenseNumber || '')
  formData.append('city', businessMeta.city || '')
  formData.append('state', businessMeta.state || '')
  formData.append('pin_code', businessMeta.pinCode || '')
  if (businessForm.imageFile) formData.append('img', businessForm.imageFile)
}

const extractBusinessIdFromResponse = (data) => {
  const payload = data?.data || data || {}
  const candidateValues = [
    data?.business_id,
    data?.id,
    data?.s_id,
    data?.insert_id,
    data?.last_id,
    data?.business_vendor_id,
    data?.vendor_business_id,
    data?.vendor_id,
    payload?.business_id,
    payload?.id,
    payload?.s_id,
    payload?.insert_id,
    payload?.last_id,
    payload?.business_vendor_id,
    payload?.vendor_business_id,
    payload?.vendor_id,
    Array.isArray(payload) ? payload[0]?.business_vendor_id : '',
    Array.isArray(payload) ? payload[0]?.vendor_business_id : '',
    Array.isArray(payload) ? payload[0]?.business_id : '',
    Array.isArray(payload) ? payload[0]?.id : '',
    Array.isArray(payload) ? payload[0]?.s_id : ''
  ]

  const match = candidateValues.find((value) => value !== undefined && value !== null && String(value).trim() !== '')
  return match ? String(match) : ''
}

const extractBusinessCollection = (data) => {
  const payload = data?.data || data || {}
  if (Array.isArray(payload)) return payload
  return payload?.vendors || payload?.business_vendors || payload?.businessVendor || payload?.items || payload?.list || []
}

const resolveCreatedBusinessId = async (responseData) => {
  const directId = extractBusinessIdFromResponse(responseData)
  if (directId) return directId

  const vendorId = getAuthUserId()
  if (!vendorId) return ''

  try {
    const formData = new FormData()
    formData.append('s_id', vendorId)
    const { data } = await api.listBusinessVendor(formData)
    const collection = extractBusinessCollection(data)

    if (!Array.isArray(collection) || !collection.length) return ''

    const normalizedBusinessName = String(businessForm.businessName || '').trim().toLowerCase()
    const normalizedOwnerName = String(businessForm.ownerName || '').trim().toLowerCase()

    const matchedBusiness = [...collection].reverse().find((item) => {
      const itemBusinessName = String(item?.business_name || item?.name || item?.title || '').trim().toLowerCase()
      const itemOwnerName = String(item?.owner_name || item?.owner || item?.contact_name || '').trim().toLowerCase()

      return itemBusinessName === normalizedBusinessName && (!normalizedOwnerName || itemOwnerName === normalizedOwnerName)
    }) || [...collection].reverse().find((item) => {
      const itemBusinessName = String(item?.business_name || item?.name || item?.title || '').trim().toLowerCase()
      return itemBusinessName === normalizedBusinessName
    })

    return matchedBusiness
      ? String(matchedBusiness?.id || matchedBusiness?.business_vendor_id || matchedBusiness?.vendor_business_id || matchedBusiness?.business_id || matchedBusiness?.s_id || '')
      : ''
  } catch {
    return ''
  }
}

const loadBusiness = async () => {
  if (!isEditMode.value) return
  loading.value = true
  loadError.value = ''
  try {
    const formData = new FormData()
    formData.append('business_id', businessId.value)
    const { data } = await api.editBusinessVendor(formData)
    const payload = Array.isArray(data?.data) ? data.data[0] : data?.data || data?.business || data?.item || null
    if (!payload) {
      throw new Error('Business not found')
    }
    populateFromBusiness(payload)
  } catch (error) {
    loadError.value = error?.response?.data?.msg || error?.response?.data?.message || error?.message || 'Failed to load business'
  } finally {
    loading.value = false
  }
}

const validateBusinessImage = (file) => {
  if (!file) return true
  if (!file.type.startsWith('image/')) {
    businessErrors.image = 'Only image files are allowed'
    return false
  }
  return true
}

const handleBusinessImageChange = (event) => {
  businessErrors.image = ''
  const file = event.target.files?.[0]
  if (!file) return
  if (!validateBusinessImage(file)) {
    event.target.value = ''
    return
  }
  revokeBusinessImagePreview()
  businessForm.imageFile = file
  businessForm.imageName = file.name
  businessImagePreview.value = URL.createObjectURL(file)
  event.target.value = ''
}

const removeBusinessImage = () => {
  revokeBusinessImagePreview()
  businessImagePreview.value = ''
  businessForm.imageFile = null
  businessForm.imageName = ''
  businessForm.existingImage = ''
  businessErrors.image = 'Business image is required.'
}

const handleDocumentChange = (event, slotKey) => {
  const file = event.target.files?.[0]
  documentErrors[slotKey] = ''
  if (!file) return
  const extension = (file.name.split('.').pop() || '').toLowerCase()
  if (!ALLOWED_DOCUMENT_EXTENSIONS.includes(extension)) {
    documentErrors[slotKey] = 'Only .jpg, .jpeg, .png, .docx, and .pdf files are allowed'
    event.target.value = ''
    return
  }
  if (file.size > MAX_DOCUMENT_SIZE) {
    documentErrors[slotKey] = 'File size must be 5 MB or less'
    event.target.value = ''
    return
  }
  revokeDocumentPreview(slotKey)
  documentsForm[slotKey] = file
  existingDocuments[slotKey] = ''
  if (getDocumentPreviewType(file) === 'image') {
    documentPreviews[slotKey] = URL.createObjectURL(file)
  }
  event.target.value = ''
}

const removeDocument = (slotKey) => {
  revokeDocumentPreview(slotKey)
  documentsForm[slotKey] = null
  existingDocuments[slotKey] = ''
  documentErrors[slotKey] = ''
}

const addTeamMember = () => {
  teamMembers.push({
    id: Date.now(),
    name: '',
    email: '',
    role: 'Staff'
  })
}

const removeTeamMember = (memberId) => {
  if (teamMembers.length === 1) return
  const index = teamMembers.findIndex((member) => member.id === memberId)
  if (index !== -1) teamMembers.splice(index, 1)
}

const addBusinessPhoneEntry = () => {
  contactForm.phoneEntries.push(createBusinessPhoneEntry())
}

const removeBusinessPhoneEntry = (index) => {
  contactForm.phoneEntries.splice(index, 1)
}

const openModuleEditor = (type, moduleId = '') => {
  if (!isEditMode.value) return
  const routeName = moduleId ? 'business-module-edit' : 'business-module-create'
  const params = moduleId
    ? { businessId: businessId.value, moduleType: type, moduleId }
    : { businessId: businessId.value, moduleType: type }

  router.push({
    name: routeName,
    params,
    query: { businessName: businessForm.businessName || 'Business' }
  })
}

const deleteVenueModule = async (module) => {
  const venueId = String(module?.venueId || module?.id || '').trim()
  if (!venueId) {
    await showErrorToast('Venue id is missing. Refresh and try again.')
    return
  }

  const result = await Swal.fire({
    title: 'Delete venue?',
    text: `This will permanently remove ${module?.name || 'this venue'}.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#c73d3d',
    cancelButtonColor: '#8c7b6b',
    confirmButtonText: 'Delete',
    cancelButtonText: 'Cancel',
    reverseButtons: true
  })

  if (!result.isConfirmed) return

  deletingVenueId.value = String(module?.id || venueId)
  formError.value = ''

  try {
    const formData = new FormData()
    formData.append('venue_id', venueId)
    if (businessId.value) formData.append('business_id', businessId.value)
    const { data } = await api.deleteVenue(formData)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) throw new Error(data?.msg || data?.message || 'Failed to delete venue')

    venueModules.value = venueModules.value.filter((item) => String(item?.id) !== String(module?.id))
    writeStoredModules('venue', venueModules.value)
    await fetchVenueModules()
    await showSuccessToast('Venue deleted successfully')
  } catch (error) {
    formError.value = error?.response?.data?.msg || error?.response?.data?.message || error?.message || 'Failed to delete venue'
    await showErrorToast(formError.value)
  } finally {
    deletingVenueId.value = ''
  }
}

const saveBusinessTab = async () => {
  formError.value = ''
  if (!validateBusinessTab()) {
    formError.value = 'Fix the highlighted business fields and try again.'
    return false
  }

  const formData = new FormData()
  if (isEditMode.value) {
    formData.append('business_id', businessId.value)
    appendBusinessInfoFields(formData)
    const { data } = await api.updateBusinessVendor(formData)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) throw new Error(data?.msg || data?.message || 'Failed to update business')
    return {
      created: false,
      businessId: businessId.value
    }
  } else {
    const vendorId = getAuthUserId()
    if (!vendorId) throw new Error('Vendor id is not available. Sign in again.')
    formData.append('s_id', vendorId)
    appendBusinessInfoFields(formData)
    const { data } = await api.addBusinessVendor(formData)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) throw new Error(data?.msg || data?.message || 'Failed to add business')
    const createdBusinessId = await resolveCreatedBusinessId(data)
    if (!createdBusinessId) {
      throw new Error('Business was created, but its id could not be resolved. Refresh the dashboard and try again.')
    }
    return {
      created: true,
      businessId: createdBusinessId
    }
  }
}

const saveContactTab = async () => {
  formError.value = ''
  if (!isEditMode.value) throw new Error('Save the business info first before adding contact & Address details.')
  if (!validateContactTab()) {
    formError.value = 'Fix the highlighted contact fields and try again.'
    return false
  }
  const formData = new FormData()
  const phonePayload = getPhonePayloadByType()
  const primaryPhoneEntry = getPrimaryPhoneEntry()
  const primaryPhoneDigits = String(primaryPhoneEntry?.number || '').replace(/\D/g, '').slice(0, 10)
  formData.append('business_id', businessId.value)
  formData.append('phone_code', primaryPhoneEntry?.type === 'landline' ? defaultCountryPhoneCode : normalizeCountryPhoneCode(primaryPhoneEntry?.phoneCode || defaultCountryPhoneCode))
  formData.append('phone', primaryPhoneDigits)
  formData.append('email', contactForm.email || '')
  formData.append('website', contactForm.website || '')
  formData.append('address', contactForm.address || '')
  formData.append('latitude', contactForm.latitude || '')
  formData.append('longitude', contactForm.longitude || '')
  formData.append('currency', contactForm.currency || '')
  formData.append('zone', contactForm.zone || '')
  formData.append('timezone', contactForm.timezone || '')
  formData.append('country', contactForm.country || '')
  formData.append('state', contactForm.state || '')
  formData.append('city', contactForm.city || '')
  formData.append('contact_person_name', String(contactForm.contactPersonName || '').trim())
  formData.append('landline_no', phonePayload.landline.join(','))
  formData.append('office_no', phonePayload.office.join(','))
  formData.append('whatsapp_no', phonePayload.whatsapp.join(','))
  const { data } = await api.updateBusinessContactVendor(formData)
  const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
  if (isError) throw new Error(data?.msg || data?.message || 'Failed to update contact & Address details')
  return true
}

const saveDocumentsTab = async () => {
  formError.value = ''
  resetDocumentErrors()
  if (!isEditMode.value) throw new Error('Save the business info first before uploading documents.')
  const hasSelectedDocuments = Object.values(documentsForm).some(Boolean)
  const hasExistingDocuments = hasExistingUploadedDocuments()
  if (!hasSelectedDocuments && !hasExistingDocuments) {
    throw new Error('Choose at least one document to upload.')
  }
  if (!hasSelectedDocuments && hasExistingDocuments) {
    return true
  }
  const formData = new FormData()
  formData.append('business_id', businessId.value)
  if (documentsForm.doc1) formData.append('doc1', documentsForm.doc1)
  if (documentsForm.doc2) formData.append('doc2', documentsForm.doc2)
  if (documentsForm.doc3) formData.append('doc3', documentsForm.doc3)
  const { data } = await api.updateBusinessDocumentsVendor(formData)
  const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
  if (isError) throw new Error(data?.msg || data?.message || 'Failed to update documents')
  await loadBusiness()
  documentsForm.doc1 = null
  documentsForm.doc2 = null
  documentsForm.doc3 = null
  revokeAllDocumentPreviews()
  return true
}

const saveBankTab = async () => {
  formError.value = ''
  if (!isEditMode.value) throw new Error('Save the business info first before adding bank details.')
  if (!validateBankTab()) {
    formError.value = 'Fix the highlighted bank fields and try again.'
    return false
  }
  const formData = new FormData()
  formData.append('business_id', businessId.value)
  formData.append('bank_name', bankForm.bankName || '')
  formData.append('account_no', bankForm.accountNumber || '')
  formData.append('ifsc_code', bankForm.ifscCode || '')
  formData.append('upi_id', bankForm.upiId || '')
  const { data } = await api.updateBusinessBankVendor(formData)
  const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
  if (isError) throw new Error(data?.msg || data?.message || 'Failed to update bank details')
  return true
}

const saveCurrentTab = async () => {
  if (activeTab.value === 'business') return saveBusinessTab()
  if (activeTab.value === 'contact') return saveContactTab()
  if (activeTab.value === 'documents') return saveDocumentsTab()
  if (activeTab.value === 'bank') return saveBankTab()
  return true
}

watch(
  () => route.query.tab,
  (tab) => {
    const requested = String(tab || 'business')
    activeTab.value = tabs.some((item) => item.id === requested) ? requested : 'business'
  },
  { immediate: true }
)

watch(
  () => route.params.id,
  () => {
    loadBusiness()
    refreshModules()
  }
)

onMounted(() => {
  loadBusiness()
  refreshModules()
  contactForm.phoneEntries = contactForm.phoneEntries.map((entry) => createBusinessPhoneEntry(entry.type, entry.phoneCode, entry.number))
  bootstrapContactLocation()
})

onBeforeUnmount(() => {
  if (locationMap.value) {
    locationMap.value.remove()
    locationMap.value = null
  }
  locationMarker.value = null
  revokeBusinessImagePreview()
  revokeAllDocumentPreviews()
})
</script>
